import React, { useCallback, useEffect, useMemo, useState, } from 'react';

import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import {
  PhonenumberMonthlyCostAddForm,
  PhonenumberMonthlyCostAddInitialValues,
  PhonenumberMonthlyCostComponents,
  PhonenumberMonthlyCostAdditionalParams,
  PhonenumberMonthlyCostFormOptions,
  onPhonenumberMonthlyCostFormSaved,
  IPhonenumberMonthlyCostFormOptionsProps,
  AlertError,
  queryParam,
} from '@Utils';
import { AtlasForm, IFormOptions, } from 'atlas-form';
import { CrudEnum, ISharedCreatePhonenumberMonthlyCost, PermissionEnum, } from 'atlas-shared';
import { createPhonenumberMonthlyCost, fetchPhonenumberMonthlyCost, getPhonenumberMonthlyCostCreateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withAdd, IWithAddProps, } from '@Hocs/with.add';
import { useAbilities, useAuth, usePhonenumberMonthlyCostParents, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithAddProps<ISharedCreatePhonenumberMonthlyCost> {}

const PhonenumberMonthlyCostFormAdd = ({ values, setValues, save, formSchema, schemaLoading, saving, initialValues, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const phonenumber_monthly_cost_parents = usePhonenumberMonthlyCostParents();
  const organizations = useOrganizations();
  const abilities = useAbilities();
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const [cloned, setCloned, ] = useState<Partial<ISharedCreatePhonenumberMonthlyCost>>();

  const param_props: IPhonenumberMonthlyCostFormOptionsProps = {
    t,
    is_new: true,
    abilities,
    phonenumber_monthly_cost_parents: phonenumber_monthly_cost_parents.phonenumber_monthly_cost_parents,
    organizations: organizations.organizations,
  };
  const formLayout = useMemo(() => PhonenumberMonthlyCostAddForm(values, param_props, t), [values, phonenumber_monthly_cost_parents, organizations, t, abilities, ]);
  const options: IFormOptions = useMemo(() => PhonenumberMonthlyCostFormOptions(values || PhonenumberMonthlyCostAddInitialValues, param_props, t), [values, phonenumber_monthly_cost_parents, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedCreatePhonenumberMonthlyCost, form) => {
    setValues(values);
    onChange?.(values, changedValue, form);
  }, [setValues, ]);
  const onFinish = useCallback((values: ISharedCreatePhonenumberMonthlyCost) => save(() => createPhonenumberMonthlyCost(values).then(
    res => onSaved ? onSaved(res, navigate) : onPhonenumberMonthlyCostFormSaved(t, 'add', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [save, navigate, t, ]);
  
  useEffect(() => {
    const id = queryParam('clone');

    if (id)
      fetchPhonenumberMonthlyCost(+id)
        .then(({
          phonenumber_monthly_cost_parent_id,
          title,
          phonenumber_monthly_cost_list,
        }) => {
          setCloned({
            phonenumber_monthly_cost_parent_id,
            title,
            phonenumber_monthly_cost_list,
          });
        });
    else
      setCloned({});
  }, []);
  
  if (schemaLoading || !cloned)
    return <AppSpinner />;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!phonenumber_monthly_cost_parents.loaded || !organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !values || abilities.can(null, CrudEnum.Insert, PermissionEnum.PhonenumberMonthlyCost, values)}
    >
      <AtlasForm
        dispatch={dispatch}
        formLayout={formLayout}
        form={formSchema}
        options={options}
        components={PhonenumberMonthlyCostComponents}
        additionalParams={PhonenumberMonthlyCostAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={{ ...(cloned || {}), ...PhonenumberMonthlyCostAddInitialValues, ...initialValues, }}
        saving={saving}
        title={title ? t(title) : `${t('ADD')} ${t('PHONENUMBER_MONTHLY_COST')}`}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const PhonenumberMonthlyCostFormAddView = React.memo(withAdd(PhonenumberMonthlyCostFormAdd, getPhonenumberMonthlyCostCreateSchema, PhonenumberMonthlyCostAddInitialValues));
